import { GoTrophy } from 'react-icons/go';
import { useLanguage } from '@/i18n/LanguageContext';
import ScrollReveal from './ScrollReveal';

const features = [
    {
        title: 'Legal Support Shouldn\u2019t Be a Privilege',
        description:
            'Legal help shouldn\u2019t depend on your income. Whether you need instant answers, free representation, or affordable consultations with qualified lawyers, Podus ensures you never face a legal problem alone.',
        img: '/assets/libs/legal-support.svg',
        icon: '/icons/legal.svg',
    },
    {
        title: 'Smarter Protection for Your Business',
        description:
            'A single unclear clause can cost your business months of revenue. Podus Contract helps you draft, review, and manage agreements with AI-powered analysis and expert review by a lawyer in the relevant field',
        img: '/assets/libs/smart-protection.svg',
        icon: '/icons/book-pen.svg',
    },
    {
        title: 'Hire with Confidence',
        description:
            'Podus is an AI-powered super app. Podus Checker reduces hiring risk with structured background checks on employment, criminal records, social media, education, and guarantors.',
        img: '/assets/libs/hire.svg',
        icon: '/icons/search.svg',
    },
];

export default function Features() {
    const { t } = useLanguage();
    return (
        <section className="px-4 py-16 lg:px-16 lg:py-24 xl:px-20">
            {/* Top Section */}
            <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:gap-16">
                {/* Left Content */}
                <div className="lg:w-[45%]">
                    {/* Badge — desktop only */}
                    <ScrollReveal variant="fade-up" delay={0} distance={20}>
                        <div className="bg-primary-50/40 text-primary mb-8 hidden items-center gap-2 rounded-full px-4 py-2 text-sm font-medium lg:inline-flex">
                            <GoTrophy className="h-4 w-4" />
                            {t('features.badge')}
                        </div>
                    </ScrollReveal>

                    {/* Heading — desktop */}
                    <ScrollReveal variant="blur-up" delay={100} duration={800}>
                        <h2 className="hidden text-[48px] font-extrabold leading-[1.2] tracking-tight lg:block xl:text-[64px]">
                            <span className="text-[#AEAEAE]">{t('features.headingGray')}</span>
                            <span className="text-secondary">{t('features.headingDark')}</span>
                        </h2>
                    </ScrollReveal>

                    {/* Heading — mobile */}
                    <ScrollReveal variant="blur-up" delay={100} duration={800}>
                        <h2 className="text-[28px] font-bold leading-[1.2] tracking-tight sm:text-[36px] lg:hidden">
                            <span className="text-[#AEAEAE]">{t('features.headingMobileGray')}</span>
                            <span className="text-secondary">{t('features.headingMobileDark')}</span>
                        </h2>
                    </ScrollReveal>

                    {/* Subtitle — desktop */}
                    <ScrollReveal variant="fade-up" delay={250}>
                        <p className="text-[#4E4D4D] mt-6 hidden max-w-md text-base font-normal leading-relaxed lg:block">
                            {t('features.subtitleDesktop')}
                        </p>
                    </ScrollReveal>

                    {/* Subtitle — mobile */}
                    <ScrollReveal variant="fade-up" delay={250}>
                        <p className="text-[#4E4D4D] mt-5 text-base font-normal leading-relaxed lg:hidden">
                            {t('features.subtitleMobile')}
                        </p>
                    </ScrollReveal>
                </div>

                {/* Right Image — desktop only */}
                <ScrollReveal
                    variant="scale"
                    delay={200}
                    duration={900}
                    className="hidden lg:block lg:w-[55%] pl-[15px]"
                >
                    <img
                        src="/assets/libs/2147224203.svg"
                        alt="Podus contract management interface"
                        className="w-full object-contain"
                    />
                </ScrollReveal>
            </div>

            {/* Feature Cards */}
            <div className="mt-12 grid grid-cols-1 gap-5 lg:mt-16 lg:grid-cols-3">
                {features.map((feature, i) => (
                    <ScrollReveal key={i} variant="fade-up" delay={i * 120} distance={30}>
                        <div className="flex flex-col overflow-hidden rounded-2xl bg-[#F5F5F5]">
                            <div className="px-6 pb-4 pt-6">
                                <div className="mb-3 flex items-center gap-3">
                                    <img src={feature.icon} alt="" className="h-[34px] w-[34px] flex-shrink-0" />
                                    <h3 className="text-secondary text-lg font-bold">{t(`features.${i}.title`)}</h3>
                                </div>
                                <p className="text-secondary-100 text-sm leading-[2] font-medium">
                                    {t(`features.${i}.description`)}
                                </p>
                            </div>
                            <div className="mt-auto flex items-end justify-center px-6">
                                <img src={feature.img} alt="Feature preview" className="w-[85%] object-contain" />
                            </div>
                        </div>
                    </ScrollReveal>
                ))}
            </div>
        </section>
    );
}
